import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Key as AuthenticatorIcon,
  VpnKey as BackupIcon,
} from '@mui/icons-material';

export type TwoFactorMethod = 'authenticator' | 'email' | 'backup';

interface TwoFactorVerificationProps {
  maskedEmail: string;
  actualEmail: string; // The real email for API calls
  expiresInMinutes: number;
  onVerify: (code: string, method?: TwoFactorMethod, rememberDevice?: boolean) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isVerifying: boolean;
  error: string | null;
  // New single-method props
  requiredMethod?: TwoFactorMethod; // The method to use now
  companyRequiredMethod?: TwoFactorMethod; // What company requires (may differ during grace)
  userHasTotp?: boolean; // Whether user has TOTP set up
  // Grace period props
  isGracePeriod?: boolean;
  gracePeriodEndsAt?: string | null;
  canUseEmailDuringGrace?: boolean;
  // Email error props
  emailError?: boolean;
  hasBackupCodes?: boolean;
  // Legacy props for backwards compatibility
  availableMethods?: TwoFactorMethod[];
  defaultMethod?: TwoFactorMethod;
}

interface MethodConfig {
  value: TwoFactorMethod;
  label: string;
  Icon: typeof EmailIcon; // The icon component
  description: string;
  codeLength: number;
  showTimer: boolean;
  canResend: boolean;
}

const METHOD_CONFIGS: Record<TwoFactorMethod, MethodConfig> = {
  authenticator: {
    value: 'authenticator',
    label: 'Autenticador',
    Icon: AuthenticatorIcon,
    description: 'Ingrese el código de 6 dígitos de su aplicación autenticadora',
    codeLength: 6,
    showTimer: false,
    canResend: false,
  },
  email: {
    value: 'email',
    label: 'Correo',
    Icon: EmailIcon,
    description: 'Le enviamos un código de 6 dígitos a',
    codeLength: 6,
    showTimer: true,
    canResend: true,
  },
  backup: {
    value: 'backup',
    label: 'Código de Respaldo',
    Icon: BackupIcon,
    description: 'Ingrese uno de sus códigos de respaldo (formato: XXXX-XXXX)',
    codeLength: 8,
    showTimer: false,
    canResend: false,
  },
};

export const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  maskedEmail,
  expiresInMinutes,
  onVerify,
  onResend,
  onBack,
  isVerifying,
  error,
  // New single-method props
  requiredMethod,
  companyRequiredMethod,
  userHasTotp = false,
  // Grace period props
  isGracePeriod = false,
  gracePeriodEndsAt,
  canUseEmailDuringGrace = false,
  // Email error props
  emailError = false,
  hasBackupCodes = false,
  // Legacy props
  availableMethods = ['email'],
  defaultMethod,
}) => {
  // Determine initial method - prefer requiredMethod if provided (new flow)
  const getInitialMethod = (): TwoFactorMethod => {
    // New single-method flow: use requiredMethod directly
    if (requiredMethod) {
      return requiredMethod;
    }
    // Legacy flow: use defaultMethod or determine from availableMethods
    if (defaultMethod && availableMethods.includes(defaultMethod)) {
      return defaultMethod;
    }
    // Prefer authenticator if available and user has it set up
    if (availableMethods.includes('authenticator') && userHasTotp) {
      return 'authenticator';
    }
    // Otherwise use first available method
    return availableMethods[0] || 'email';
  };

  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>(
    emailError && hasBackupCodes ? 'backup' : getInitialMethod()
  );
  const currentConfig = METHOD_CONFIGS[selectedMethod];
  const [backupCode, setBackupCode] = useState('');

  // Format grace period end date
  const formatGracePeriodEnd = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const [code, setCode] = useState<string[]>(Array(currentConfig.codeLength).fill(''));
  // Opt-in: skip 2FA on this device for 7 days. Captured at submit time.
  const [rememberDevice, setRememberDevice] = useState(false);
  const [timeLeft, setTimeLeft] = useState(expiresInMinutes * 60);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset code when method changes
  useEffect(() => {
    const newConfig = METHOD_CONFIGS[selectedMethod];
    setCode(Array(newConfig.codeLength).fill(''));
    inputRefs.current = [];
    // Focus first input after method change
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [selectedMethod]);

  // Countdown timer for code expiry (only for email method)
  useEffect(() => {
    if (!currentConfig.showTimer || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, currentConfig.showTimer]);

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < currentConfig.codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === currentConfig.codeLength - 1 && newCode.every((d) => d !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, currentConfig.codeLength);

    if (pastedData.length === currentConfig.codeLength) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[currentConfig.codeLength - 1]?.focus();
      // Auto-submit on paste
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (codeString?: string) => {
    const finalCode = codeString || code.join('');
    if (finalCode.length !== currentConfig.codeLength) return;
    await onVerify(finalCode, selectedMethod, rememberDevice);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending || !currentConfig.canResend) return;

    setIsResending(true);
    setResendError(null);

    try {
      await onResend();
      setResendCooldown(60); // 60 second cooldown
      setTimeLeft(expiresInMinutes * 60); // Reset expiry timer
      setCode(Array(currentConfig.codeLength).fill('')); // Clear code
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      // Handle rate limiting
      if (err.waitSeconds) {
        setResendCooldown(err.waitSeconds);
        setResendError(`Espere ${err.waitSeconds} segundos antes de solicitar un nuevo código.`);
      } else {
        setResendError(err.message || 'No se pudo reenviar el código');
      }
    } finally {
      setIsResending(false);
    }
  };

  const isExpired = currentConfig.showTimer && timeLeft <= 0;

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: (theme) => theme.palette.background.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <currentConfig.Icon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Typography variant="h5" align="center" fontWeight="bold">
                Verificación de Dos Factores
              </Typography>
            </Box>

            {/* Email Error Warning */}
            {emailError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="medium">
                  Error al enviar el correo
                </Typography>
                <Typography variant="body2">
                  No pudimos enviarle el código de verificación por correo (límite de envíos excedido).
                  {hasBackupCodes
                    ? ' Use uno de sus códigos de respaldo para iniciar sesión.'
                    : ' Para tener acceso de emergencia, genere códigos de respaldo desde Perfil → Seguridad después de iniciar sesión.'}
                </Typography>
              </Alert>
            )}

            {/* Grace Period Warning */}
            {isGracePeriod && canUseEmailDuringGrace && companyRequiredMethod === 'authenticator' && selectedMethod === 'email' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="medium">
                  Período de gracia activo
                </Typography>
                <Typography variant="body2">
                  Su empresa exige usar una aplicación autenticadora. Tiene hasta el{' '}
                  <strong>{formatGracePeriodEnd(gracePeriodEndsAt)}</strong> para configurarla.
                  Mientras tanto, le enviamos el código por correo como respaldo.
                </Typography>
              </Alert>
            )}

            {/* Method Description */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {currentConfig.description}
              </Typography>
              {selectedMethod === 'email' && (
                <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                  {maskedEmail}
                </Typography>
              )}
              {selectedMethod === 'authenticator' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  (Google Authenticator, Authy, etc.)
                </Typography>
              )}
            </Box>

            {/* Method-specific tip */}
            {selectedMethod === 'email' && !emailError && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={false}>
                <Typography variant="caption" color="text.secondary">
                  El correo llega en menos de un minuto. Si no aparece, revise la carpeta de spam o solicite un nuevo código.
                </Typography>
              </Alert>
            )}
            {selectedMethod === 'authenticator' && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={false}>
                <Typography variant="caption" color="text.secondary">
                  Abra su aplicación autenticadora, ubique la cuenta de Safety y copie el código de 6 dígitos. El código cambia cada 30 segundos.
                </Typography>
              </Alert>
            )}
            {selectedMethod === 'backup' && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={false}>
                <Typography variant="caption" color="text.secondary">
                  Cada código de respaldo se puede usar una sola vez. Si no le quedan códigos, contacte al supervisor de su empresa para que le genere un enlace de recuperación.
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {resendError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {resendError}
              </Alert>
            )}

            {currentConfig.showTimer && (
              isExpired ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  El código expiró. Solicite uno nuevo.
                </Alert>
              ) : (
                <Typography
                  variant="body2"
                  align="center"
                  color={timeLeft < 60 ? 'error' : 'text.secondary'}
                  sx={{ mb: 2 }}
                >
                  El código expira en: {formatTime(timeLeft)}
                </Typography>
              )
            )}

            {/* Opt-in to skip 2FA on this device for 7 days. Placed above the
                code entry so the auto-submit on the last digit doesn't beat it. */}
            <FormControlLabel
              sx={{ mb: 1.5, alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  disabled={isVerifying}
                  size="small"
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Recordar este dispositivo por 7 días</Typography>
                  <Typography variant="caption" color="text.secondary">
                    No le pediremos el código de nuevo en este navegador durante 7 días.
                  </Typography>
                </Box>
              }
            />

            {/* Backup code input — single text field */}
            {selectedMethod === 'backup' ? (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  disabled={isVerifying}
                  slotProps={{
                    htmlInput: {
                      maxLength: 9,
                      style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: 4 },
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && backupCode.length >= 8) {
                      onVerify(backupCode, 'backup', rememberDevice);
                    }
                  }}
                />
              </Box>
            ) : (
              /* Normal 6-digit code input */
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 3,
                  flexWrap: 'wrap',
                }}
                onPaste={handlePaste}
              >
                {code.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isVerifying || isExpired}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: (theme) => theme.palette.background.primary,
                        },
                      },
                    }}
                    slotProps={{
                      input: {
                        sx: {
                          width: 48,
                          height: 56,
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                        },
                      },
                      htmlInput: {
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]',
                        style: { textAlign: 'center' },
                        'aria-label': `Digito ${index + 1} de ${currentConfig.codeLength}`,
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (selectedMethod === 'backup') {
                  onVerify(backupCode, 'backup', rememberDevice);
                } else {
                  handleSubmit();
                }
              }}
              disabled={
                selectedMethod === 'backup'
                  ? backupCode.length < 8 || isVerifying
                  : code.some((d) => d === '') || isVerifying || isExpired
              }
              sx={{
                mb: 2,
                py: 1.5,
                bgcolor: (theme) => theme.palette.background.buttonBg,
                color: (theme) => theme.palette.background.buttonText,
                '&:hover': {
                  bgcolor: (theme) => theme.palette.background.buttonBg,
                  filter: 'brightness(0.9)',
                }
              }}
            >
              {isVerifying ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verificar'
              )}
            </Button>

            {/* Resend (email method only) */}
            {currentConfig.canResend && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ¿No recibió el código?
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || isResending}
                  startIcon={isResending ? <CircularProgress size={16} /> : <RefreshIcon />}
                  sx={{ color: (theme) => theme.palette.background.primary }}
                >
                  {resendCooldown > 0
                    ? `Reenviar en ${resendCooldown}s`
                    : isResending
                    ? 'Enviando...'
                    : 'Reenviar código'}
                </Button>
              </Box>
            )}

            {/* Switch to backup codes */}
            {selectedMethod !== 'backup' && (
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setSelectedMethod('backup')}
                  underline="hover"
                  sx={{ cursor: 'pointer', color: (theme) => theme.palette.background.primary }}
                >
                  Usar un código de respaldo
                </Link>
              </Box>
            )}

            {/* Switch back from backup codes */}
            {selectedMethod === 'backup' && !emailError && (
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setSelectedMethod(getInitialMethod())}
                  underline="hover"
                  sx={{ cursor: 'pointer', color: (theme) => theme.palette.background.primary }}
                >
                  Volver a verificación por {getInitialMethod() === 'email' ? 'correo' : 'autenticador'}
                </Link>
              </Box>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={onBack}
                underline="hover"
                sx={{
                  cursor: 'pointer',
                  color: (theme) => theme.palette.background.primary,
                }}
              >
                Volver al inicio de sesión
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default TwoFactorVerification;
